import {
  ArrowRight,
  Check,
  CheckCheck,
  Cloud,
  FileCheck2,
  Fingerprint,
  Hexagon,
  LockKeyhole,
  Monitor,
  ShieldCheck,
  ShieldX,
  Smartphone,
  X,
  type LucideIcon,
} from 'lucide-react';
import { EthIcon, UsdcIcon } from '@/components/ui/token-icons';
import { cn } from '@/lib/utils';
import {
  getSecurityCreationDraft,
  SECURITY_BLOCKED_DRAFT_COUNT,
  SECURITY_CREATION_INITIATED_PHASE,
  SECURITY_WORKFLOW_EXAMPLE as EXAMPLE,
} from './security-workflow-data';

const SCREEN_NAMES = [
  'Transaction creation',
  'Member approval',
  'Guardian validation',
  'Onchain verification',
];

type SceneProps = { phase: number };

function PaymentSummary() {
  return (
    <div className="swf-payment">
      <span className="swf-payment-token">
        <UsdcIcon width={27} height={27} />
      </span>
      <div>
        <strong>
          {EXAMPLE.amount} <span>USDC</span>
        </strong>
        <span>To {EXAMPLE.recipient}</span>
      </div>
      <ArrowRight />
    </div>
  );
}

function CheckRow({
  title,
  detail,
  checked,
  failed = false,
}: {
  title: string;
  detail: string;
  checked: boolean;
  failed?: boolean;
}) {
  return (
    <div
      className={cn(
        'swf-check-row',
        checked && 'is-checked',
        checked && failed && 'is-failed'
      )}
    >
      <span className="swf-check-mark">
        {checked ? failed ? <X /> : <Check /> : <span />}
      </span>
      <div>
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      <span className="swf-check-state">
        {checked ? (failed ? 'Blocked' : 'Verified') : 'Checking'}
      </span>
    </div>
  );
}

function Outcome({
  done,
  blocked = false,
  pending,
  children,
}: {
  done: boolean;
  blocked?: boolean;
  pending: string;
  children: React.ReactNode;
}) {
  const Icon = blocked ? ShieldX : CheckCheck;
  return (
    <div
      className={cn(
        'swf-outcome',
        done && 'is-complete',
        done && blocked && 'is-blocked'
      )}
    >
      {done ? <Icon /> : <LockKeyhole />}
      <span>{done ? children : pending}</span>
    </div>
  );
}

function CreateTransaction({ phase }: SceneProps) {
  const draft = getSecurityCreationDraft(phase);
  const withinPolicy = draft.invalidField === null;
  const initiated = phase >= SECURITY_CREATION_INITIATED_PHASE;
  const recipientBlocked = draft.invalidField === 'recipient';
  const AssetIcon = draft.asset === 'ETH' ? EthIcon : UsdcIcon;

  return (
    <>
      <div className="swf-scene-heading">
        <div>
          <span>Transaction builder</span>
          <h4>Create a transaction</h4>
        </div>
        <span
          className={cn(
            'swf-badge',
            initiated ? 'is-done' : withinPolicy ? 'is-ready' : 'is-blocked'
          )}
        >
          {initiated ? 'Initiated' : withinPolicy ? 'Ready' : 'Blocked'}
        </span>
      </div>
      <div className="swf-creation-policy">
        <ShieldCheck />
        <div>
          <span>Selected policy</span>
          <strong>{EXAMPLE.policy}</strong>
        </div>
        <span>{EXAMPLE.requiredApprovals} approvals required</span>
      </div>
      <div className="swf-creation-fields">
        <div
          className={
            draft.invalidField === 'asset' ? 'is-blocked' : 'is-allowed'
          }
        >
          <span>Asset</span>
          <strong>
            <AssetIcon width={19} height={19} /> {draft.asset}
          </strong>
        </div>
        <div
          className={
            draft.invalidField === 'amount' ? 'is-blocked' : 'is-allowed'
          }
        >
          <span>Amount</span>
          <strong>{draft.amount}</strong>
        </div>
        <div
          className={cn(
            'swf-creation-recipient',
            recipientBlocked ? 'is-blocked' : 'is-allowed'
          )}
        >
          <span>Recipient</span>
          <strong>
            {draft.recipient}
            {recipientBlocked ? <ShieldX /> : <Check />}
          </strong>
        </div>
      </div>
      <div className="swf-creation-limits">
        Approved vendors · {EXAMPLE.dailyCap} USDC daily limit
      </div>
      <div className="swf-creation-check">
        <span>
          {withinPolicy
            ? 'Request within policy'
            : `Blocked draft ${phase + 1} of ${SECURITY_BLOCKED_DRAFT_COUNT}`}
        </span>
        <CheckRow
          title={draft.title}
          detail={draft.detail}
          checked
          failed={!withinPolicy}
        />
      </div>
      <div
        className={cn(
          'swf-create-action',
          !withinPolicy && 'is-disabled',
          withinPolicy && !initiated && 'is-ready',
          initiated && 'is-initiated'
        )}
      >
        {initiated ? (
          <CheckCheck />
        ) : withinPolicy ? (
          <ArrowRight />
        ) : (
          <LockKeyhole />
        )}
        {initiated
          ? 'Transaction initiated. Awaiting approvals.'
          : 'Initiate transaction'}
      </div>
    </>
  );
}

function Signers({ phase }: SceneProps) {
  const approvalCount = Math.max(0, phase - 1);
  return (
    <div className="swf-signing-scene">
      <div className="swf-phone">
        <span className="swf-phone-notch" />
        <div className="swf-phone-heading">
          <ShieldCheck />
          <span>Review payment</span>
        </div>
        <PaymentSummary />
        <div className="swf-phone-policy">
          <Fingerprint />
          <span>Policy checked on your device</span>
        </div>
        <div className={cn('swf-phone-checks', phase >= 1 && 'is-checked')}>
          <span>
            <Check /> Approved recipient
          </span>
          <span>
            <Check /> Within the daily spending limit
          </span>
        </div>
        <div className="swf-signers-heading">
          <span>Member approvals</span>
          <strong>
            {approvalCount} <span>of</span> {EXAMPLE.members.length}
          </strong>
        </div>
        <div className="swf-signers">
          {EXAMPLE.members.map((name, index) => (
            <div
              key={name}
              className={cn('swf-member', index < approvalCount && 'is-signed')}
            >
              <span>
                {name[0]}
                {index < approvalCount && <Check />}
              </span>
              <strong>{name}</strong>
              <small>{index < approvalCount ? 'Signed' : 'Pending'}</small>
            </div>
          ))}
        </div>
        <Outcome done={phase >= 3} pending="Waiting for member signatures">
          Required signatures collected
        </Outcome>
      </div>
    </div>
  );
}

function Guardian({ phase }: SceneProps) {
  return (
    <>
      <div className="swf-scene-heading">
        <div>
          <span>Independent check</span>
          <h4>Guardian validation</h4>
        </div>
        <span className={cn('swf-badge', phase >= 3 && 'is-done')}>
          {phase >= 3 ? 'Verified' : 'Checking'}
        </span>
      </div>
      <PaymentSummary />
      <div className="swf-service">
        <span>
          <Cloud />
        </span>
        <div>
          <strong>Guardian service</strong>
          <span>Re-runs the selected policy independently</span>
        </div>
      </div>
      <div className="swf-check-list">
        <CheckRow
          title="Recipient"
          detail="On the approved vendor list"
          checked={phase >= 1}
        />
        <CheckRow
          title="Spending limit"
          detail={`Within ${EXAMPLE.dailyCap} USDC per day`}
          checked={phase >= 2}
        />
        <CheckRow
          title="Member signatures"
          detail={`${EXAMPLE.requiredApprovals} of ${EXAMPLE.members.length} members approved`}
          checked={phase >= 3}
        />
      </div>
      <Outcome done={phase >= 3} pending="Checking before submission">
        Guardian validation complete
      </Outcome>
    </>
  );
}

function Contracts({ phase }: SceneProps) {
  return (
    <>
      <div className="swf-scene-heading">
        <div>
          <span>Final execution check</span>
          <h4>Verified onchain</h4>
        </div>
        <span className={cn('swf-badge', phase >= 3 && 'is-done')}>
          {phase >= 3 ? 'Executed' : 'Verifying'}
        </span>
      </div>
      <PaymentSummary />
      <div className="swf-chain-visual">
        <span>
          <FileCheck2 />
        </span>
        <i />
        <span className="swf-contract-node">
          <Hexagon />
          <ShieldCheck />
        </span>
        <i />
        <span className={cn(phase >= 3 && 'is-done')}>
          <CheckCheck />
        </span>
      </div>
      <div className="swf-check-list">
        <CheckRow
          title="Policy proof"
          detail={EXAMPLE.policy}
          checked={phase >= 1}
        />
        <CheckRow
          title="Required signatures"
          detail={`${EXAMPLE.requiredApprovals} of ${EXAMPLE.members.length} member signatures verified`}
          checked={phase >= 2}
        />
        <CheckRow
          title="Transaction rules"
          detail="Recipient, token, amount, and limit"
          checked={phase >= 3}
        />
      </div>
      <Outcome
        done={phase >= 3}
        pending="Funds stay in the wallet until checks pass"
      >
        {EXAMPLE.amount} USDC payment executed
      </Outcome>
    </>
  );
}

const LAYERS: { name: string; Icon: LucideIcon }[] = [
  { name: 'Web app', Icon: Monitor },
  { name: 'Your device', Icon: Smartphone },
  { name: 'Guardian', Icon: Cloud },
  { name: 'Onchain', Icon: Hexagon },
];

/**
 * Illustrative standard-execution scenes, independent of live accounts.
 * Creation rejects an invalid draft before approvals. Only the corrected
 * request continues through the device, Guardian, and onchain checks.
 */
export function SecurityWorkflowMockup({
  step,
  phase,
}: {
  step: number;
  phase: number;
}) {
  const Scene =
    [CreateTransaction, Signers, Guardian, Contracts][step] ??
    CreateTransaction;
  return (
    <figure
      className="twf-figure"
      aria-label={`Illustrative ${SCREEN_NAMES[step]?.toLowerCase()} screen`}
    >
      <div className="twf-window" aria-hidden="true">
        <div className="twf-window-bar">
          <img src="/den-logo.svg" alt="" />
        </div>
        <div className="swf-surface">
          <div key={step} className="swf-scene twf-enter">
            <Scene phase={phase} />
          </div>
          <div className="swf-layer-track">
            {LAYERS.map(({ name, Icon }, index) => (
              <span key={name} className={cn(index === step && 'is-active')}>
                {index < step ? <Check /> : <Icon />}
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </figure>
  );
}
